import { useTranslation } from 'next-i18next'
import React from 'react'

import { useAttributes } from '@hozana/dom/hooks/useAttributes'
import { useIsHydrated } from '@hozana/hooks/useIsHydrated'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { QA } from '@hozana/tests/constants'
import { TRACKING_SOURCE } from '@hozana/tracking/constants'
import { GTM } from '@hozana/tracking/gtm'

import { Button } from 'elements/button/Button'
import { Block } from 'elements/layout/Block'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { Space } from 'elements/layout/Space'
import { Icon } from 'elements/media/Icon'
import { Ellipsis } from 'elements/text/Ellipsis'
import { P } from 'elements/text/P'
import { Text } from 'elements/text/Text'
import { Chevron } from 'elements/ui/Chevron'

import { ICON } from 'config/icons'
import { reportAbuse } from 'general/routines'
import { Popover } from 'general/structure/Popover'
import { PopoverButton } from 'general/structure/Popover/PopoverButton'
import { newRoutes } from 'routes/constants'

import { useIntentionsWidget } from 'modules/intention/hooks/useIntentionsWidget'
import { selectWidgetIntention } from 'modules/intention/selectors'
import type { TIntention } from 'modules/intention/types'
import { PRAY_OBJECT_TYPE } from 'modules/pray/constants'
import { prayUser } from 'modules/pray/routines'
import { UserAvatar } from 'modules/user/components/UserAvatar'
import { UserName } from 'modules/user/components/UserName'

type TPopoverContent = {
  closePopover?: VoidFunction
  widgetLang: string
  intentionId: number
}

export const PopoverContent: TFC<TPopoverContent> = ({ closePopover, widgetLang, intentionId }) => {
  const dispatch = useDispatch()
  const attrs = useAttributes() // used for accessibility to pass "tabindex = -1" to avoid focus

  const onReportAbuse = () => {
    closePopover()
    dispatch(
      reportAbuse({
        objectId: intentionId,
        objectType: 'intention',
        lang: widgetLang,
      }),
    )
  }
  return (
    <PopoverButton icon={ICON.WARNING_SOLID} onClick={onReportAbuse} {...attrs}>
      trans:intention:app.report-abuse.report
    </PopoverButton>
  )
}

export type TIntentionItemProps = {
  intention: TIntention
  trackingSource: string
  withWrapper?: boolean
}

export const IntentionItem: TFC<TIntentionItemProps> = ({ intention, trackingSource, withWrapper }) => {
  const isHydrated = useIsHydrated()
  const widgetIntention = useSelector(selectWidgetIntention)
  const { id: widgetId, lang: widgetLang } = useIntentionsWidget() || {}
  const withPopover = isHydrated && !widgetId

  const attrs = useAttributes() // used for accessibility to pass "tabindex = -1" to avoid focus
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const onPrayIntention = () => {
    dispatch(
      prayUser({
        objectType: PRAY_OBJECT_TYPE.INTENTION,
        objectId: intention.id,
        trackingSource,
        widgetId,
        guestName: widgetIntention?.guestName,
      }),
    )
  }

  return (
    <>
      {withPopover && (
        <Popover
          top="40px"
          right="10px"
          w={{ sm: '225px' }}
          triggerComponent={
            <Chevron
              onClick={() => {
                GTM.trackEvent(GTM.EVENTS.CHEVRON, { trackingSource })
              }}
              label={t('intention:word.options')}
              ariaLabel={t('intention:intention.options')}
              {...attrs}
            />
          }
        >
          <PopoverContent widgetLang={widgetLang} intentionId={intention.id} />
        </Popover>
      )}
      <Div p="10px 5px" as={withWrapper ? Block : undefined}>
        <Row nowrap gutter="10px">
          {withPopover && (
            // on Hozana site
            <Cell minW="50px">
              <UserAvatar userId={intention.user} />
            </Cell>
          )}
          <Cell align="middleLeft" flex="1" m="0 30px 0 0" noOverflow>
            {intention.guestName ? (
              // intention posted from widget
              <Ellipsis bold vAlign="bottom">
                {intention.guestName}
              </Ellipsis>
            ) : (
              // intention posted by a connected user
              <UserName userId={intention.user} bold vAlign="bottom" />
            )}
          </Cell>
        </Row>
        <P align="left">{intention.content}</P>
        <Space h="5px" />
        {intention.hasPrayed ? (
          <Div h="33px">
            <P color="yellow" align="center">
              <Ellipsis>trans:intention:intention.prayed.already</Ellipsis>
              <br />
              <Text bold>trans:intention:intention.prayed.thanks</Text>
            </P>
          </Div>
        ) : (
          <Row>
            <Cell flex="1" align="middleLeft">
              <Ellipsis color="black30" fontSize="0.9em">
                <Icon name={ICON.FIRE} color="black30" m="0 2px 0 0" size="1.4em" vAlign="inherit" />
                {intention.prayersCount || 0}
              </Ellipsis>
            </Cell>
            <Cell>
              {!isHydrated ? (
                <Button
                  data-testid={QA.INTENTION.PRAY_BUTTON}
                  valueLink={{
                    to: newRoutes.intentions({ source: TRACKING_SOURCE.INTENTION }),
                    reload: true,
                  }}
                  colors="yellow"
                  size="small"
                  {...attrs}
                >
                  trans:intention:pray.i-pray.short
                </Button>
              ) : (
                <Button
                  colors="yellow"
                  onClick={onPrayIntention}
                  size="small"
                  {...attrs}
                  data-testid={QA.INTENTION.PRAY_BUTTON}
                >
                  trans:intention:pray.i-pray.short
                </Button>
              )}
            </Cell>
          </Row>
        )}
      </Div>
    </>
  )
}
